import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  ChevronDown,
  ChevronRight,
  CreditCard,
  Headphones,
  MapPin,
  Package,
  PhoneCall,
  RotateCcw,
  Search as SearchIcon,
  ShieldCheck,
  ShoppingCart,
  Smile,
  Truck,
  UserCog,
  Users,
  X,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import DecorLeaf from '../components/DecorLeaf';
import usePageMetadata from '../hooks/usePageMetadata';
import { PRODUCT_RANGES, STORE } from '../config/store';
import { focusRing, homeRail } from '../styles/ui';

// ─── FAQ content ────────────────────────────────────────────────────────────
// Every answer describes something this application actually does, or repeats
// information already published on the site. Nothing here states a policy the
// project does not hold: no refund windows, no delivery-time promises, no
// discount commitments, no opening hours.
const CATEGORIES = [
  { id: 'orders', label: 'Orders & Delivery', hint: 'Track, delivery time, and related queries', icon: Truck },
  { id: 'products', label: 'Products & Quality', hint: 'Freshness, sourcing and product details', icon: Package },
  { id: 'cancellations', label: 'Cancellations & Refunds', hint: 'Returns, cancellations and refunds', icon: RotateCcw },
  { id: 'payments', label: 'Payments', hint: 'Payment methods and the wallet', icon: CreditCard },
  { id: 'wholesale', label: 'Wholesale & Bulk Orders', hint: 'For businesses and resellers', icon: Building2 },
  { id: 'store', label: 'Store Information', hint: 'Our store, location and contact', icon: MapPin },
  { id: 'account', label: 'Account & Settings', hint: 'Manage your account and preferences', icon: UserCog },
];

// The steps of the real ordering flow, shown under the first answer.
const ORDER_STEPS = [
  { icon: SearchIcon, label: '1. Browse or search for products' },
  { icon: ShoppingCart, label: '2. Add items to cart' },
  { icon: Truck, label: '3. Choose delivery slot & address' },
  { icon: CreditCard, label: '4. Pay securely & place order' },
];

const FAQS = [
  {
    category: 'orders',
    question: 'How do I place an order?',
    answer:
      'Browse the categories or search for what you need, add items to your cart, then open the cart and continue to checkout. You will be asked to sign in before the order is placed.',
    steps: ORDER_STEPS,
  },
  {
    category: 'orders',
    question: 'How do I choose a delivery slot?',
    answer:
      'Checkout shows the delivery slots that are currently available for your address and you pick one before placing the order. If none are showing, no slots are open at that moment — try again a little later.',
  },
  {
    category: 'orders',
    question: 'Do you deliver to my area?',
    answer:
      'Serviceability is checked against the pincode on your delivery address at checkout. If we do not deliver there yet, checkout will tell you so you can try another address.',
  },
  {
    category: 'orders',
    question: 'How can I track my order?',
    answer:
      'Open My Orders from your account, then select an order to see its current status. Once an order is on its way, its detail page shows live delivery tracking.',
  },
  {
    category: 'orders',
    question: 'Can I cancel or modify my order?',
    answer:
      'Open the order from My Orders: while the backend still allows it, its detail page offers a cancel option that starts a conversation with our team. Items cannot be edited after placing — cancel and order again instead.',
  },
  {
    category: 'products',
    question: 'What products does Fresh & Homely offer?',
    answer: `We stock ${PRODUCT_RANGES.slice(0, -1).join(', ')} and ${PRODUCT_RANGES.slice(
      -1
    )}. Every cut is selected daily, cleaned and hygienically packed before it reaches you.`,
  },
  {
    category: 'products',
    question: 'Why does the final weight of some items vary?',
    answer:
      'Some fresh cuts are sold by catch weight, so the exact weight is confirmed when your order is packed and the price is calculated from the weight actually supplied.',
  },
  {
    category: 'cancellations',
    question: 'Can I cancel an order?',
    answer:
      'Open the order from My Orders and use the cancel option on its detail page. That starts a conversation with our team about the cancellation rather than cancelling silently.',
  },
  {
    category: 'cancellations',
    question: 'How is a refund handled?',
    answer:
      'Refunds are arranged through that same cancellation conversation, where you are asked to choose how you would like the refund returned to you. Our team will confirm the details with you there.',
  },
  {
    category: 'payments',
    question: 'Which payment methods can I use?',
    answer:
      'Checkout offers cash on delivery and online payment. Orders containing catch-weight items are placed as cash on delivery, because the final amount depends on the weight supplied.',
  },
  {
    category: 'payments',
    question: 'What is the wallet on my account?',
    answer:
      'Your account carries a wallet with its balance and recent transactions, which you can review from the account page.',
  },
  {
    category: 'wholesale',
    question: 'Do you supply wholesale or bulk orders?',
    answer:
      'Yes. We supply fresh fish, dairy, meat and grocery essentials in bulk at wholesale rates, with regular supply and quality checks on every order.',
  },
  {
    category: 'wholesale',
    question: 'How do businesses discuss bulk requirements?',
    answer: `Call us on ${STORE.phone} to talk through quantities, pricing and delivery schedules for your business.`,
  },
  {
    category: 'store',
    question: 'Where is the store located?',
    answer: `You will find us at ${STORE.address}. Use Get Directions to open the exact location in Google Maps.`,
  },
  {
    category: 'store',
    question: 'What are your opening hours?',
    answer: `Timings are not listed on this site, so please call us on ${STORE.phone} to confirm when we are open before you travel.`,
  },
  {
    category: 'store',
    question: 'How can I contact the store?',
    answer: `The quickest way to reach us is by phone on ${STORE.phone}. You are also welcome to walk in and speak to our team directly.`,
  },
  {
    category: 'account',
    question: 'How do I manage my delivery addresses?',
    answer:
      'Your saved addresses live on the account page, where you can add one, edit it, remove it or set the address used by default at checkout.',
  },
  {
    category: 'account',
    question: 'Where are my saved favourites?',
    answer:
      'Items you add to your wishlist are kept in Favourites on your account, so you can find them again on your next visit.',
  },
];

// Decorative value statements beside the hero artwork — not clickable, because
// none of them is a separate destination.
const SUPPORT_PILLS = [
  { icon: Headphones, label: 'Quick Support', hint: 'Get instant help' },
  { icon: Users, label: 'Friendly Team', hint: "We're always ready" },
  { icon: ShieldCheck, label: 'Reliable Solutions', hint: 'Fast & effective' },
  { icon: Smile, label: 'Happier You', hint: 'Your satisfaction matters' },
];

// Each chip resolves to something real: a route the app has, or a FAQ category
// that actually holds answers.
const POPULAR_TOPICS = [
  { label: 'Track Order', to: '/orders', icon: Truck },
  { label: 'Delivery', category: 'orders', icon: Package },
  { label: 'Cancellations', category: 'cancellations', icon: X },
  { label: 'Payments', category: 'payments', icon: CreditCard },
  { label: 'Bulk Orders', category: 'wholesale', icon: Building2 },
];

const chipClass = `inline-flex items-center gap-1.5 h-[30px] px-3 rounded-full bg-white border border-[#DCE8DC] text-[12.5px] font-semibold text-emerald-950 whitespace-nowrap shrink-0 shadow-[0_2px_6px_-4px_rgba(15,51,24,0.3)] hover:bg-emerald-50 transition-colors ${focusRing}`;
const outlinedBtn = `inline-flex items-center justify-center gap-2 h-[44px] px-6 rounded-full bg-white border border-[#2E8B47] text-[#1E5A30] text-[14px] font-bold hover:bg-emerald-50 transition-colors ${focusRing}`;

const SupportPage = () => {
  const [query, setQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [category, setCategory] = useState(CATEGORIES[0].id);
  const [openQuestion, setOpenQuestion] = useState(FAQS[0].question);
  const faqRef = useRef(null);

  usePageMetadata({
    title: 'Support | Fresh & Homely',
    description:
      'Get help from Fresh & Homely in Bengaluru. Call the store, find our address and directions, and read answers to common questions about orders, products and wholesale supply.',
    canonicalPath: '/support',
  });

  // Searching looks across every category; browsing filters to one. Both run
  // against the same local FAQ content — there is no help-search endpoint.
  const visibleFaqs = useMemo(() => {
    const term = submittedQuery.trim().toLowerCase();
    if (!term) return FAQS.filter((faq) => faq.category === category);
    return FAQS.filter(
      (faq) =>
        faq.question.toLowerCase().includes(term) || faq.answer.toLowerCase().includes(term)
    );
  }, [submittedQuery, category]);

  const focusFaqs = () => {
    faqRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const runSearch = (event) => {
    event.preventDefault();
    const trimmed = query.trim();
    setSubmittedQuery(trimmed);
    if (trimmed) {
      setOpenQuestion(null);
      focusFaqs();
    }
  };

  const chooseCategory = (id) => {
    setCategory(id);
    setSubmittedQuery('');
    setQuery('');
    const first = FAQS.find((faq) => faq.category === id);
    setOpenQuestion(first ? first.question : null);
  };

  const resetFaqs = () => {
    chooseCategory(CATEGORIES[0].id);
    focusFaqs();
  };

  const activeCategoryLabel = CATEGORIES.find((item) => item.id === category)?.label;

  return (
    <>
      <Navbar />

      <main className="bg-white">
        {/* ── Hero ───────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden pt-20 bg-[linear-gradient(180deg,#F4FAF2_0%,#F7FBF5_60%,#FFFFFF_100%)]">
          <div className="pointer-events-none select-none absolute inset-x-0 top-20 bottom-0" aria-hidden="true">
            <DecorLeaf className="hidden xl:block absolute left-[90px] top-[50px] w-9 text-emerald-500/80 -rotate-[20deg]" />
            <DecorLeaf className="hidden xl:block absolute left-[130px] top-[300px] w-7 text-emerald-600/70 rotate-[24deg]" />
            <DecorLeaf className="hidden xl:block absolute right-[60px] top-[170px] w-8 text-emerald-500/70 rotate-[14deg]" />
            {/* Only where the viewport leaves room outside the rail; at 1440
                it would sit on top of the value pills. */}
            <img src="/pages/support-note-right.webp" alt="" width="106" height="140" className="hidden 2xl:block absolute right-[4px] top-[184px] w-[100px]" />
          </div>

          <div className={`${homeRail} relative`}>
            {/* At xl the hero is a fixed 350px stage: the copy column on the
                left, the illustration anchored to the stage's bottom edge and
                the value pills down the right, as in the design. */}
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-4 xl:h-[362px]">
              <div className="relative z-10 w-full lg:w-[46%] xl:w-[654px] shrink-0 pt-[22px] pb-6 lg:pb-0 xl:pl-[94px]">
                <span className="inline-flex items-center gap-1.5 h-[22px] px-3 rounded-full bg-[#E4F3E5] text-emerald-800 text-[10px] font-bold tracking-[0.16em] uppercase">
                  <Headphones size={11} aria-hidden="true" />
                  Customer Support
                </span>
                <h1 className="serif mt-[8px] text-[38px] sm:text-[48px] xl:text-[54px] font-bold text-emerald-950 leading-[0.95]">
                  How can we
                  <DecorLeaf className="inline-block w-7 ml-2 -mt-6 text-emerald-600 rotate-[20deg]" />
                  <br />
                  <span className="text-[#1E8A3B]">help you today?</span>
                </h1>
                <p className="mt-[12px] max-w-[450px] text-[15.5px] leading-[21px] text-[#4B5563]">
                  We&apos;re here to make your experience with Fresh &amp; Homely smooth and
                  hassle-free. Whether you have a question about our products, need help with an
                  order, or want to partner with us — our team is always happy to help.
                </p>

                <form onSubmit={runSearch} role="search" aria-label="Search help topics" className="mt-[14px] max-w-[515px]">
                  <div className="flex items-center gap-2 h-[44px] pl-4 pr-1 bg-white rounded-full border border-emerald-100 shadow-[0_6px_18px_-8px_rgba(15,51,24,0.2)]">
                    <SearchIcon size={18} className="text-emerald-950 shrink-0" strokeWidth={2.2} aria-hidden="true" />
                    <input
                      type="text"
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      aria-label="Search for help"
                      placeholder="Search for help… (e.g. delivery, refund, bulk order)"
                      className="flex-1 min-w-0 h-full bg-transparent outline-none text-[13.5px] text-ink-primary placeholder:text-[#8A9199] text-ellipsis pl-1"
                    />
                    <button
                      type="submit"
                      className={`shrink-0 inline-flex items-center justify-center h-[36px] px-6 rounded-full bg-[linear-gradient(90deg,#2E8B47,#1E7A3C)] text-white font-semibold text-[14px] shadow-[0_4px_10px_-2px_rgba(46,139,71,0.45)] ${focusRing}`}
                    >
                      Search
                    </button>
                  </div>
                </form>

                <div className="flex flex-wrap xl:flex-nowrap items-center gap-x-2 gap-y-2 mt-[12px] xl:w-[600px] relative z-10">
                  <span className="text-[13px] font-bold text-emerald-950 mr-0.5">Popular:</span>
                  {POPULAR_TOPICS.map(({ label, to, category: topicCategory, icon: Icon }) =>
                    to ? (
                      <Link key={label} to={to} className={chipClass}>
                        <Icon size={13} className="text-emerald-800" aria-hidden="true" />
                        {label}
                      </Link>
                    ) : (
                      <button
                        key={label}
                        type="button"
                        onClick={() => {
                          chooseCategory(topicCategory);
                          focusFaqs();
                        }}
                        className={chipClass}
                      >
                        <Icon size={13} className="text-emerald-800" aria-hidden="true" />
                        {label}
                      </button>
                    )
                  )}
                </div>
              </div>

              {/* Support artwork: the design's own illustration of a support
                  representative with a produce crate (never presented as a
                  real employee), with the value pills beside it as HTML. */}
              <div className="relative flex-1 min-w-0 lg:self-end xl:static" aria-hidden="true">
                <img
                  src="/pages/support-hero.webp"
                  alt=""
                  width="542"
                  height="355"
                  className="block w-full max-w-[542px] mx-auto h-auto xl:absolute xl:left-[596px] xl:bottom-0 xl:mx-0 xl:h-[350px] xl:w-auto xl:max-w-none"
                />
                <ul className="mt-4 grid grid-cols-2 gap-2 xl:mt-0 xl:absolute xl:right-[24px] xl:top-[24px] xl:w-[200px] xl:grid-cols-1 xl:gap-[8px]">
                  {SUPPORT_PILLS.map(({ icon: Icon, label, hint }) => (
                    <li key={label} className="flex items-center gap-2.5 h-[55px] rounded-2xl bg-white border border-[#E6EEE6] shadow-[0_6px_16px_-10px_rgba(15,51,24,0.3)] px-3">
                      <span className="w-[34px] h-[34px] rounded-full bg-[#E4F3E5] text-emerald-700 flex items-center justify-center shrink-0">
                        <Icon size={17} strokeWidth={1.9} />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[12.5px] font-bold text-emerald-950 leading-tight">{label}</span>
                        <span className="block text-[10.5px] text-ink-secondary leading-tight mt-[2px] xl:whitespace-nowrap">{hint}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ── Common Questions ───────────────────────────────────────────── */}
        <section ref={faqRef} className="relative overflow-hidden pt-[22px] pb-[18px] bg-white scroll-mt-24">
          <div className="pointer-events-none select-none absolute inset-0" aria-hidden="true">
            <img src="/pages/support-left.webp" alt="" loading="lazy" width="192" height="415" className="hidden xl:block absolute left-0 top-[16px] w-[192px]" />
            <img src="/pages/support-right.webp" alt="" loading="lazy" width="196" height="400" className="hidden xl:block absolute right-0 top-[32px] w-[196px]" />
          </div>

          <div className={`${homeRail} relative`}>
            <div className="xl:px-[94px]">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="serif text-[28px] xl:text-[36px] font-bold text-emerald-950 leading-tight">Common Questions</h2>
                  <p className="text-[14.5px] text-[#4B5563] mt-[4px]">Find quick answers to the most asked questions.</p>
                </div>
                <button
                  type="button"
                  onClick={resetFaqs}
                  className={`inline-flex items-center gap-1.5 mt-[12px] text-[13px] font-bold text-[#1E7A3C] hover:text-emerald-900 ${focusRing}`}
                >
                  View all FAQs
                  <ArrowRight size={14} strokeWidth={2.5} aria-hidden="true" />
                </button>
              </div>

              <div className="mt-[16px] grid grid-cols-1 lg:grid-cols-[330px_minmax(0,1fr)] gap-5 items-start">
                <ul className="flex lg:flex-col gap-2 lg:gap-0 overflow-x-auto no-scrollbar lg:overflow-visible lg:rounded-2xl lg:bg-white lg:border lg:border-[#E6EEE6] lg:shadow-[0_6px_18px_-10px_rgba(15,51,24,0.25)] lg:divide-y lg:divide-[#EEF3EE]">
                  {CATEGORIES.map(({ id, label, hint, icon: Icon }) => {
                    const active = !submittedQuery && id === category;
                    return (
                      <li key={id} className="shrink-0 lg:shrink">
                        <button
                          type="button"
                          onClick={() => chooseCategory(id)}
                          aria-pressed={active}
                          className={`w-full flex items-center gap-3 h-[52px] px-3 text-left transition-colors ${focusRing} ${
                            active
                              ? 'bg-[#1E7A3C] text-white lg:rounded-t-2xl'
                              : 'bg-white text-emerald-950 rounded-2xl lg:rounded-none hover:bg-emerald-50/70'
                          }`}
                        >
                          <span className={`w-[34px] h-[34px] rounded-xl flex items-center justify-center shrink-0 ${active ? 'bg-white/15 text-white' : 'bg-[#E4F3E5] text-emerald-700'}`}>
                            <Icon size={17} aria-hidden="true" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-[13px] font-bold leading-tight whitespace-nowrap">{label}</span>
                            <span className={`hidden lg:block text-[11px] mt-[2px] truncate ${active ? 'text-white/85' : 'text-ink-secondary'}`} aria-hidden="true">{hint}</span>
                          </span>
                          <ChevronRight size={15} className={`hidden lg:block shrink-0 ${active ? 'text-white' : 'text-emerald-300'}`} aria-hidden="true" />
                        </button>
                      </li>
                    );
                  })}
                </ul>

                <div>
                  {submittedQuery && (
                    <p className="text-[13px] text-ink-secondary mb-3" role="status">
                      {visibleFaqs.length === 0
                        ? `No answers matched “${submittedQuery}”. Try another word, or call us on ${STORE.phone}.`
                        : `${visibleFaqs.length} answer${
                            visibleFaqs.length === 1 ? '' : 's'
                          } matched “${submittedQuery}”.`}
                    </p>
                  )}

                  <div className="flex flex-col gap-2.5">
                    {visibleFaqs.map((faq) => {
                      const isOpen = openQuestion === faq.question;
                      const id = faq.question.replace(/\W+/g, '-').toLowerCase();
                      return (
                        <div
                          key={faq.question}
                          className={`rounded-2xl border bg-white overflow-hidden transition-colors ${
                            isOpen ? 'border-[#D7E7D7] bg-[#FBFDFB] shadow-[0_6px_18px_-12px_rgba(15,51,24,0.3)]' : 'border-[#E6EEE6]'
                          }`}
                        >
                          <h3>
                            <button
                              type="button"
                              id={`faq-q-${id}`}
                              aria-expanded={isOpen}
                              aria-controls={`faq-a-${id}`}
                              onClick={() => setOpenQuestion(isOpen ? null : faq.question)}
                              className={`w-full flex items-center gap-3 text-left px-3.5 h-[48px] text-[14.5px] font-bold text-emerald-950 hover:bg-emerald-50/50 transition-colors ${focusRing}`}
                            >
                              <span className="w-[28px] h-[28px] rounded-full bg-[#1E7A3C] text-white text-[11.5px] font-bold flex items-center justify-center shrink-0" aria-hidden="true">
                                Q
                              </span>
                              <span className="flex-1">{faq.question}</span>
                              <ChevronDown
                                size={18}
                                aria-hidden="true"
                                className={`text-emerald-900 shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                              />
                            </button>
                          </h3>
                          {isOpen && (
                            <div
                              id={`faq-a-${id}`}
                              role="region"
                              aria-labelledby={`faq-q-${id}`}
                              className="px-3.5 pl-[54px] pb-4 text-[13px] text-[#4B5563] leading-[1.55]"
                            >
                              {faq.answer}
                              {faq.steps && (
                                <ol className="mt-3 grid grid-cols-2 xl:grid-cols-4 gap-2 items-center" aria-label="Ordering steps">
                                  {faq.steps.map(({ icon: Icon, label }, index) => (
                                    <li key={label} className="relative flex items-center gap-2.5 rounded-xl border border-[#E6EEE6] bg-white px-3 h-[58px]">
                                      <span className="w-9 h-9 rounded-full bg-[#E4F3E5] text-emerald-700 flex items-center justify-center shrink-0">
                                        <Icon size={17} aria-hidden="true" />
                                      </span>
                                      <span className="text-[11.5px] leading-[1.3] text-[#4B5563]">{label}</span>
                                      {index < faq.steps.length - 1 && (
                                        <ArrowRight size={13} className="hidden xl:block absolute -right-[13px] text-emerald-400" aria-hidden="true" />
                                      )}
                                    </li>
                                  ))}
                                </ol>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {!submittedQuery && (
                    <p className="sr-only" aria-live="polite">
                      Showing {activeCategoryLabel} questions.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Still need help ────────────────────────────────────────────── */}
        <section className="pb-[20px] bg-white">
          <div className={homeRail}>
            <div className="relative overflow-hidden rounded-[22px] bg-[linear-gradient(90deg,#EAF5E6_0%,#F3FAF0_45%,#F7FBF4_100%)] border border-[#E3EEE3] xl:h-[176px]">
              <div className="pointer-events-none select-none absolute inset-0" aria-hidden="true">
                <img src="/pages/support-agent.webp" alt="" loading="lazy" width="434" height="134" className="hidden xl:block absolute left-0 bottom-0 h-[130px] w-auto" />
                <img src="/pages/support-note-banner.webp" alt="" loading="lazy" width="150" height="134" className="hidden xl:block absolute right-0 bottom-0 h-[130px] w-auto" />
                <DecorLeaf className="hidden xl:block absolute left-[430px] top-[36px] w-8 text-emerald-500/80 rotate-[24deg]" />
              </div>
              <div className="relative flex flex-col xl:flex-row items-center xl:items-center gap-6 xl:gap-0 px-6 py-8 xl:py-0 xl:pl-[432px] xl:pr-[152px] h-full">
                <span className="block text-center xl:flex-1 xl:pr-6">
                  <h2 className="inline-flex items-center h-[22px] px-3 rounded-full bg-[#E4F3E5] text-emerald-800 text-[10px] font-bold tracking-[0.14em] uppercase">
                    Still need help?
                  </h2>
                  <p className="serif mt-[6px] text-[24px] xl:text-[30px] font-bold text-emerald-950 leading-tight whitespace-nowrap">
                    Our team is just a call away!
                  </p>
                  <p className="mt-[4px] text-[13.5px] text-[#4B5563]">
                    We&apos;re here to help you with orders, products, partnerships or anything else.
                  </p>
                  <span className="mt-[6px] flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
                    <address className="text-[11.5px] text-ink-secondary not-italic">{STORE.address}</address>
                    <Link to="/orders" className={`inline-flex items-center gap-1 text-[12px] font-semibold text-[#1E7A3C] whitespace-nowrap ${focusRing}`}>
                      Go to My Orders
                      <ArrowRight size={12} aria-hidden="true" />
                    </Link>
                  </span>
                </span>
                <div className="flex flex-col gap-2.5 w-full max-w-[256px] shrink-0">
                  <a
                    href={STORE.phoneHref}
                    className={`inline-flex items-center justify-between gap-3 h-[44px] px-5 rounded-full bg-[linear-gradient(90deg,#3FA84A,#1E7A3C)] text-white text-[14px] font-bold shadow-[0_8px_18px_-6px_rgba(46,139,71,0.55)] ${focusRing}`}
                  >
                    <PhoneCall size={17} aria-hidden="true" />
                    <span className="flex-1 text-center">Call {STORE.phone}</span>
                    <ArrowRight size={16} aria-hidden="true" />
                  </a>
                  <a href={STORE.mapsUrl} target="_blank" rel="noopener noreferrer" className={`${outlinedBtn} justify-between px-5`}>
                    <MapPin size={17} aria-hidden="true" />
                    <span className="flex-1 text-left">
                      <span className="block leading-tight">Get Directions</span>
                      <span className="block text-[10.5px] font-medium text-ink-secondary leading-tight">Visit us in store</span>
                    </span>
                    <ArrowRight size={16} aria-hidden="true" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default SupportPage;
